import {
  DrawerDescription,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  Typography,
  FormInput,
  Loading,
  Button,
  Drawer,
  Switch,
  Label,
  Form,
} from '@ui/components';
import { CompanyDetailsDto } from '@interfaces/models';
import { useHasPermission } from '@shared/hooks';
import { Cancel } from '@ui/components/icons';
import { FC } from 'react';

import { useCompanyAccountManagement } from './use-company-account-management';
import { AddressForm } from './address-form';

export const CompanyAccountManagementSidebar: FC<{
  companyId: CompanyDetailsDto['id'];
  onClose?: () => void;
  isOpened?: boolean;
}> = ({ companyId, onClose = () => {}, isOpened = false }) => {
  const {
    isCorrespondenceAddressVisible,
    isCompanyDataLoading,
    isUpdatePending,
    form,
    t,
    showCorrespondenceAddress,
    hideCorrespondenceAddress,
    onSubmit,
  } = useCompanyAccountManagement(companyId, {
    onSuccess: onClose,
  });

  const hasEditPermission = useHasPermission('COMPANY_ACCOUNT_EDIT', 'WRITE');
  const nip = form.watch('nip');

  return (
    <Drawer onClose={onClose} open={isOpened} handleOnly>
      <DrawerContent className="flex flex-col gap-0 overflow-hidden p-0" draggable="false">
        <DrawerHeader className="flex flex-row items-center justify-between p-4 border-b border-border-weak">
          <DrawerTitle>{t('COMPANY_ACCOUNT_MANAGEMENT.MANAGE_COMPANY_ACCOUNT')}</DrawerTitle>
          <DrawerDescription />
          <DrawerClose asChild>
            <Button variant="ghost" size="small" icon>
              <Cancel />
            </Button>
          </DrawerClose>
        </DrawerHeader>
        <form className="flex flex-1 overflow-auto flex-col p-4 pt-5" onSubmit={onSubmit}>
          <div className="flex-1">
            <Loading isLoading={isCompanyDataLoading}>
              <Form {...form}>
                <div className="space-y-5 border-b border-border-weak pb-4">
                  <Typography variant="labelMediumBold">
                    {t('COMPANY_ACCOUNT_MANAGEMENT.COMPANY_DETAILS')}
                  </Typography>
                  <div className="space-y-6">
                    <div className="grid grid-cols-6 gap-2 items-center">
                      <Label className="col-span-2" htmlFor="companyName">
                        <Typography variant="labelSmallBook" className="text-weak" element="label">
                          {t('COMPANY_ACCOUNT_MANAGEMENT.COMPANY_NAME')}
                        </Typography>
                      </Label>
                      <FormInput
                        componentProps={{
                          id: 'companyName',
                        }}
                        placeholder={t('COMPANY_ACCOUNT_MANAGEMENT.COMPANY_NAME')}
                        disabled={!hasEditPermission}
                        className="w-full col-span-4"
                        formControl={form.control}
                        name="companyName"
                        size="small"
                        required
                      />

                      <Label className="col-span-2">
                        <Typography variant="labelSmallBook" className="text-weak" element="label">
                          {t('COMPANY_ACCOUNT_MANAGEMENT.NIP')}
                        </Typography>
                      </Label>
                      <Typography
                        className="col-span-4 text-disabled h-10 flex items-center"
                        variant="labelMediumBook"
                        element="span"
                      >
                        {nip}
                      </Typography>

                      <AddressForm
                        disabled={!hasEditPermission}
                        prefix="primaryAddress"
                        control={form.control}
                      />
                    </div>

                    <div className="space-y-5">
                      <div className="flex items-center justify-between">
                        <Typography variant="labelMediumBold">
                          {t('COMPANY_ACCOUNT_MANAGEMENT.CORRESPONDENCE_DATA')}
                        </Typography>

                        <Switch
                          onCheckedChange={(checked) =>
                            checked ? showCorrespondenceAddress() : hideCorrespondenceAddress()
                          }
                          checked={isCorrespondenceAddressVisible}
                          disabled={!hasEditPermission}
                        />
                      </div>
                      {isCorrespondenceAddressVisible && (
                        <div className="grid grid-cols-6 gap-2 items-center">
                          <AddressForm
                            prefix="correspondenceAddress"
                            disabled={!hasEditPermission}
                            control={form.control}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Form>
            </Loading>
          </div>
          <Button
            disabled={isUpdatePending || isCompanyDataLoading || !hasEditPermission}
            isLoading={isUpdatePending || isCompanyDataLoading}
            className="shrink-0"
            type="submit"
            size="large"
            wide
          >
            {t('COMPANY_ACCOUNT_MANAGEMENT.SAVE_CHANGES')}
          </Button>
        </form>
      </DrawerContent>
    </Drawer>
  );
};
